import React, { useCallback } from 'react';
import {
	type Clause,
	type KeywordOperand,
	type Operand,
	type ValueOperand,
	CLAUSE_TYPE_TERMINAL,
	OPERAND_TYPE_KEYWORD,
	OPERAND_TYPE_VALUE,
	OPERATOR_LIKE,
	OPERATOR_EQUALS,
	OPERATOR_IN,
} from '@atlaskit/jql-ast';
import type { FieldType } from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import removeFieldClauseFromJql from '@atlassian/jira-jql-utils/src/utils/remove-field-clause.tsx';
import {
	type OnSearchWrapper,
	type PropTypes,
	TextAreaPropTypes,
} from '../../../../common/types.tsx';
import { isTextAreaPropType } from '../../../../common/utils.tsx';
import { useAST } from '../../../../controllers/ast/index.tsx';
import { useSendOptionAnalytics } from '../../../../utils/send-option-analytics.tsx';
import AqlPickerPopup from './aql-picker-popup/index.tsx';
import NumberPickerPopup from './number-picker-popup/index.tsx';
import TextAreaPickerPopup from './text-area-picker-popup/index.tsx';
import { getUpdatedJql, getUpdatedJqlWithAql, getAqlFromClause } from './utils.tsx';

const isSingleValueOperand = (operand: Operand): operand is ValueOperand | KeywordOperand =>
	operand.operandType === OPERAND_TYPE_VALUE || operand.operandType === OPERAND_TYPE_KEYWORD;

type TextAreaPickerProps = {
	fieldId: string;
	fieldLabel?: string;
	fieldType: FieldType;
	activePopupKey: string;
	clause: Clause | undefined;
	onSearchWrapper?: OnSearchWrapper;
	onClear?: (onClearJqlClause: string | undefined) => void;
	onOpen: () => void;
	onClose: () => void;
	fieldDescription: string | null;
	propType: PropTypes;
	isIrremovable?: boolean;
};

export const TextAreaPicker = ({
	clause,
	fieldId,
	fieldLabel,
	fieldType,
	activePopupKey,
	onSearchWrapper,
	onClear,
	onOpen,
	onClose,
	fieldDescription,
	propType,
	isIrremovable,
}: TextAreaPickerProps): React.JSX.Element => {
	const [ast] = useAST();
	const sendOptionAnalytics = useSendOptionAnalytics();

	if (!isTextAreaPropType(propType)) {
		throw new Error(`Unsupported TextAreaPropType: ${propType}`);
	}
	const handleOnClear = useCallback(() => {
		onClear && onClear(clause ? removeFieldClauseFromJql(ast, clause) : undefined);
	}, [onClear, clause, ast]);

	const commonProps = {
		fieldId,
		fieldDisplayName: fieldLabel || '',
		fieldType,
		propType,
		isOpen: activePopupKey === fieldId,
		onOpen,
		onClose,
		onClear: handleOnClear,
		value:
			(clause &&
				clause.clauseType === CLAUSE_TYPE_TERMINAL &&
				clause.operand &&
				isSingleValueOperand(clause.operand) &&
				clause.operand.value) ||
			'',
		onSendOptionAnalytics: sendOptionAnalytics,
		fieldDescription,
		isIrremovable,
	};

	switch (propType) {
		case TextAreaPropTypes.Number:
			return (
				<NumberPickerPopup
					{...commonProps}
					onChange={(newValue) => {
						onSearchWrapper &&
							onSearchWrapper(getUpdatedJql(ast, clause, fieldId, newValue, OPERATOR_EQUALS));
					}}
				/>
			);
		case TextAreaPropTypes.Aql:
			return (
				<AqlPickerPopup
					{...commonProps}
					value={getAqlFromClause(clause)}
					onChange={(newValue) => {
						onSearchWrapper &&
							onSearchWrapper(getUpdatedJqlWithAql(ast, clause, fieldId, newValue, OPERATOR_IN));
					}}
				/>
			);
		case TextAreaPropTypes.Text:
			return (
				<TextAreaPickerPopup
					{...commonProps}
					onChange={(newValue) => {
						onSearchWrapper &&
							onSearchWrapper(getUpdatedJql(ast, clause, fieldId, newValue, OPERATOR_LIKE));
					}}
				/>
			);
		case TextAreaPropTypes.ExactText:
			return (
				<TextAreaPickerPopup
					{...commonProps}
					onChange={(newValue) => {
						onSearchWrapper &&
							onSearchWrapper(getUpdatedJql(ast, clause, fieldId, newValue, OPERATOR_EQUALS));
					}}
				/>
			);
		default:
			throw new Error(`Unsupported TextAreaPropType: ${propType}`);
	}
};
